import { useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { useBackGuard, type BackGuard } from '@/hooks/use-back-guard';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { conclaveApi } from '@/lib/conclave-api';
import { giveAskApi, giveAskHeading, type GiveAskItem } from '@/lib/give-ask-api';
import type { ConclaveTableMate } from './ConclaveTableMateCard';
import {
  beneficiaryPayload,
  emptyBeneficiary,
  isBeneficiaryValid,
  type BeneficiaryValue,
} from '@/lib/beneficiary';
import { BeneficiaryFields } from './beneficiary-fields';


interface SendPlainLeadSheetProps {
  eventId: string;
  mate: ConclaveTableMate | null;
  onOpenChange: (open: boolean) => void;
}

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function SendPlainLeadSheet({ eventId, mate, onOpenChange }: SendPlainLeadSheetProps) {
  const [title, setTitle] = useState('');
  const [requirement, setRequirement] = useState('');
  const [beneficiary, setBeneficiary] = useState<BeneficiaryValue>(emptyBeneficiary());
  const notesRef = useRef<HTMLDivElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [myGives, setMyGives] = useState<GiveAskItem[]>([]);
  const [showGives, setShowGives] = useState(false);
  const [fulfillers, setFulfillers] = useState<string[]>([]);
  const [discardOpen, setDiscardOpen] = useState(false);
  const pendingBack = useRef<BackGuard | null>(null);
  const dirty = Boolean(
    title.trim() || requirement.trim() || beneficiary.name.trim() || fulfillers.length > 0
  );
  const requestClose = () => {
    if (mate === null) return;
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };
  useBackGuard(mate !== null, (back) => {
    if (!dirty) {
      onOpenChange(false);
      return;
    }
    pendingBack.current = back;
    setDiscardOpen(true);
  });

  useEffect(() => {
    setTitle('');
    setRequirement('');
    setBeneficiary(emptyBeneficiary());
    setFulfillers([]);
    setShowGives(false);
  }, [mate?.registrationId]);

  useEffect(() => {
    if (!mate) return;
    giveAskApi
      .listOwn('GIVE')
      .then((data) => setMyGives(data.items))
      .catch(() => setMyGives([]));
  }, [mate]);

  const submit = async () => {
    if (!mate?.userId) return;
    if (title.trim().length < 5) {
      toast.error('Give the lead a title of at least 5 characters');
      return;
    }
    if (!isBeneficiaryValid(beneficiary)) {
      toast.error('Enter the name of the person this lead is for');
      return;
    }
    if (requirement.trim().length < 10) {
      toast.error('Describe the lead in at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      await conclaveApi.createLead(eventId, {
        toUserId: mate.userId,
        title: title.trim(),
        requirement: requirement.trim(),
        fulfillers,
        ...beneficiaryPayload(beneficiary),
      });
      toast.success(`Lead sent to ${mate.name}`);
      onOpenChange(false);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send the lead'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <ConfirmDialog
      open={discardOpen}
      title="Discard this lead?"
      description="The details and note you wrote will be lost."
      confirmLabel="Discard"
      destructive
      onCancel={() => {
        pendingBack.current?.stay();
        pendingBack.current = null;
        setDiscardOpen(false);
      }}
      onConfirm={() => {
        pendingBack.current = null;
        setDiscardOpen(false);
        onOpenChange(false);
      }}
    />
    <Sheet open={mate !== null} onOpenChange={(next) => { if (!next) requestClose(); }}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>{mate ? `Send a lead to ${mate.name}` : 'Send a lead'}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setShowGives((v) => !v)}>
              {showGives ? 'Hide my gives' : 'Pick from my gives'}
            </Button>
            {showGives ? (
              <div className="space-y-2">
                {myGives.length === 0 ? (
                  <p className="text-sm text-muted-foreground">You have no gives yet.</p>
                ) : (
                  myGives.map((give) => (
                    <button
                      key={give.id}
                      type="button"
                      onClick={() => {
                        setBeneficiary({
                          beneficiary: 'other',
                          name: give.contactName ?? '',
                          phone: give.contactPhone ?? '',
                          email: give.contactEmail ?? '',
                          relation: '',
                        });
                        setTitle(giveAskHeading(give).slice(0, 100));
                        setRequirement(give.description.slice(0, 1000));
                        setFulfillers(
                          give.category
                            ? [[give.category, give.subCategory].filter(Boolean).join(' > ')]
                            : []
                        );
                        setShowGives(false);
                        requestAnimationFrame(() =>
                          notesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        );
                      }}
                      className="block w-full rounded-xl border bg-white p-3 text-left"
                    >
                      <span className="block font-medium">{giveAskHeading(give)}</span>
                      <span className="block text-sm text-muted-foreground line-clamp-2">{give.description}</span>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>
          <BeneficiaryFields value={beneficiary} onChange={setBeneficiary} />
          <div ref={notesRef} className="space-y-4">
            <Input placeholder="Lead title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} />
            <Textarea
              placeholder="What do you need, or who could they introduce you to?"
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              maxLength={1000}
            />
            <Button className="w-full" onClick={submit} disabled={submitting || !mate?.userId}>
              {submitting ? 'Sending…' : 'Send lead'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
}
